/**
 * @fileoverview Hook for building institution-related alerts
 * @description Transforms institution status data into alert objects
 * that can be passed to the generic AlertBanner component.
 */
import { useMemo, useContext } from "react";
import { intl, LanguageBaseContext } from "dmsbasereact";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import DescriptionIcon from '@mui/icons-material/Description';
import { InstitutionStatusEnum as UserApprovalStatusEnum } from "./useInstitutionStatus";
import { InstitutionStatusEnum } from "../constants/InstitutionStatus";
import { APP_ROUTES } from "../utils/NamedRoutes";

/**
 * useInstitutionAlerts - Build alerts from institution status data
 * @param {Object} params - Parameters
 * @param {Array} params.institutionStatuses - Array of institution approval statuses
 * @param {boolean} params.hasAtLeastOneActive - Whether at least one institution is active
 * @param {boolean} params.hasExpiredDocuments - Whether institution has expired documents
 * @param {boolean} params.hasExpiringDocuments - Whether institution has expiring documents
 * @param {string} params.expiringDocNames - Names of expiring documents
 * @returns {Array} Array of alert objects for AlertBanner
 */
export const useInstitutionAlerts = ({
  institutionStatuses = [],
  hasAtLeastOneActive = false,
  hasExpiredDocuments = false,
  hasExpiringDocuments = false,
  expiringDocNames = ""
}) => {
  // Get current language to trigger re-render when translations load/change
  const { language } = useContext(LanguageBaseContext);

  return useMemo(() => {
    const alerts = [];

    // Check institutions pending document upload (institution itself needs documents)
    const institutionsPendingDocuments = institutionStatuses.filter(i =>
      i.institution_status === InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD
    );

    // Check institutions in registration phase (institution itself is pending approval)
    const institutionsInRegistration = institutionStatuses.filter(i =>
      i.institution_status && [
        InstitutionStatusEnum.PENDING_APPROVAL,
        InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA,
        InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO,
        InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA,
      ].includes(i.institution_status)
    );

    // User approval statuses (user's status at each institution)
    // NOTE: PENDING_TRAINING and PENDING_RECYCLING_TRAINING are handled by useTrainingAlerts hook
    const pendingUserApprovals = institutionStatuses.filter(i =>
      [
        UserApprovalStatusEnum.PENDING_DOCUMENT_UPLOAD,
        UserApprovalStatusEnum.PENDING_APPROVAL,
        UserApprovalStatusEnum.PENDING_APPROVAL_GESTAO_LENA,
        UserApprovalStatusEnum.PENDING_APPROVAL_REGULATORIO
      ].includes(i.status)
    );

    const rejectedInstitutions = institutionStatuses.filter(i =>
      i.status === UserApprovalStatusEnum.REJECTED
    );

    // Filter out institutions in registration or pending documents from pending user approvals
    const allInstitutionLevelPending = [...institutionsPendingDocuments, ...institutionsInRegistration];
    const pendingUserApprovalsFiltered = pendingUserApprovals.filter(
      inst => !allInstitutionLevelPending.some(reg => reg.institution_pk === inst.institution_pk)
    );

    // 1. Institution Pending Document Upload (specific banner)
    if (institutionsPendingDocuments.length > 0) {
      const institutionNames = institutionsPendingDocuments.map(inst => inst.institution_name).join(", ");
      alerts.push({
        id: "institution-pending-documents",
        severity: "warning",
        icon: <DescriptionIcon fontSize="inherit" />,
        message: intl.get("Institution.PendingDocuments.Message").d("Pending document submission for institution registration:") + ` ${institutionNames}.`,
        link: {
          to: APP_ROUTES.InstitutionCentral,
          label: intl.get("Institution.PendingDocuments.Link").d("My Institutions")
        }
      });
    }

    // 2. Institution In Registration Phase (approval pending)
    if (institutionsInRegistration.length > 0) {
      const institutionNames = institutionsInRegistration.map(inst => inst.institution_name).join(", ");
      const message = institutionsInRegistration.length === 1
        ? intl.get("Institution.InRegistration.Message")
            .d("The institution {institutionName} is in registration phase. The system is in read-only mode. Contact your institution administrator for more information.")
            .replace("{institutionName}", institutionNames)
        : intl.get("Institution.InRegistration.Multiple.Message")
            .d("The institutions {institutionNames} are in registration phase. The system is in read-only mode. Contact your institutions administrator for more information.")
            .replace("{institutionNames}", institutionNames);

      alerts.push({
        id: "institution-registration",
        severity: "warning",
        icon: <WarningAmberIcon fontSize="inherit" />,
        message
      });
    }

    // 3. Pending User Approval
    if (pendingUserApprovalsFiltered.length > 0) {
      const institutionNames = pendingUserApprovalsFiltered.map(inst => inst.institution_name).join(", ");
      const readOnlyMessage = hasAtLeastOneActive
        ? ""
        : ` ${intl.get("Institution.PendingApproval.ReadOnly").d("The system is in read-only mode until approval is completed.")}`;
      alerts.push({
        id: "user-approval-pending",
        severity: hasAtLeastOneActive ? "info" : "warning",
        icon: <WarningAmberIcon fontSize="inherit" />,
        title: intl.get("Institution.PendingApproval.Title").d("Awaiting approval for institutions:"),
        message: `${institutionNames}.${readOnlyMessage}`
      });
    }

    // 4. Inactive Institutions
    const inactiveInstitutions = institutionStatuses.filter(i =>
      i.institution_status === InstitutionStatusEnum.INACTIVE
    );

    if (inactiveInstitutions.length > 0) {
      const institutionNames = inactiveInstitutions.map(inst => inst.institution_name).join(", ");
      const allAreInactive = institutionStatuses.every(i => i.institution_status === InstitutionStatusEnum.INACTIVE);
      const readOnlySuffix = allAreInactive
        ? ` ${intl.get("Institution.Inactive.ReadOnly").d("You are in read-only mode.")}`
        : "";

      const baseMessage = inactiveInstitutions.length === 1
        ? intl.get("Institution.Inactive.Message")
            .d("The institution {institutionName} is inactive.")
            .replace("{institutionName}", institutionNames)
        : intl.get("Institution.Inactive.Multiple.Message")
            .d("The institutions {institutionNames} are inactive.")
            .replace("{institutionNames}", institutionNames);

      alerts.push({
        id: "institution-inactive",
        severity: "error",
        icon: <ErrorIcon fontSize="inherit" />,
        message: `${baseMessage}${readOnlySuffix}`
      });
    }

    // 5. Rejected Institutions
    if (rejectedInstitutions.length > 0) {
      const institutionNames = rejectedInstitutions.map(inst => inst.institution_name).join(", ");
      alerts.push({
        id: "user-rejected",
        severity: "error",
        icon: <ErrorIcon fontSize="inherit" />,
        title: intl.get("Institution.Rejected.Title").d("Rejected at institutions:"),
        message: `${institutionNames}. ${intl.get("Institution.Rejected.Contact").d("Contact your institution administrator for more information.")}`
      });
    }

    // 6. No Active Institutions (fallback)
    const showNoActiveBanner = !hasAtLeastOneActive && institutionStatuses.length > 0 &&
      pendingUserApprovals.length === 0 && rejectedInstitutions.length === 0 &&
      institutionsInRegistration.length === 0 && institutionsPendingDocuments.length === 0 &&
      inactiveInstitutions.length === 0;

    if (showNoActiveBanner) {
      alerts.push({
        id: "no-active-institutions",
        severity: "warning",
        icon: <WarningAmberIcon fontSize="inherit" />,
        message: intl.get("Institution.NoActive.Message").d("You are not active at any institution. The system is in read-only mode.")
      });
    }

    // 6. Expired Documents
    if (hasExpiredDocuments) {
      alerts.push({
        id: "documents-expired",
        severity: "error",
        icon: <ErrorIcon fontSize="inherit" />,
        message: intl.get("Institution.Documents.Expired.Banner").d("Institution documents have expired. The system is in read-only mode.")
      });
    }

    // 7. Expiring Documents (dismissible)
    if (hasExpiringDocuments && !hasExpiredDocuments) {
      alerts.push({
        id: "documents-expiring",
        severity: "warning",
        icon: <DescriptionIcon fontSize="inherit" />,
        dismissible: true,
        title: intl.get("Institution.Documents.ExpiringSoon.Banner").d("Institution documents expiring soon:"),
        message: `${expiringDocNames}. ${intl.get("Institution.Documents.ExpiringSoon.Action").d("Renew them to avoid interruptions.")}`
      });
    }

    return alerts;
  }, [institutionStatuses, hasAtLeastOneActive, hasExpiredDocuments, hasExpiringDocuments, expiringDocNames, language]); // eslint-disable-line react-hooks/exhaustive-deps -- language is required for i18n updates
};

export default useInstitutionAlerts;
